import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import dayjs from 'dayjs';

import type { SimpleSelectHandle } from '@/components/ui/simple-select';
import { showAlert } from '@/slice/AlertSlice';
import { Variants } from '@/types/common';
import type { Columns, ComponentData, Filters } from '@/types/table';
import { TableStateType } from '@/types/property.types';
import { getAPIUrls } from '@/util/env';
import { downloadBlob } from '@/util/downloadFile';
import { getDeepCopy } from '@/util/formatting';
import { getFileFailureDataAPI } from '../../service/apiService';
import type { UploadApiResponse } from '../../hooks/useBulkImport';
import {
  getTableData,
  patchPropertyData,
  processTableColumns
} from './importHistoryService';
import { parsePropertyErrors, parseOrderErrors } from './historyErrorParser';
import { useNavigationService } from '@/services/navigate-service';
import { ROUTES } from '@/config/routes';
import TABLE_FILTERS from '../metadata/filters';
import { States } from '../States';

export interface ImportHistoryProps {
  apiResponseData: UploadApiResponse | null;
  isProperty?: boolean;
  setFileId?: (v: string | null) => void;
  setUploadedByInfo?: (v: string) => void;
  setFileName?: (v: string) => void;
  setDateInfo?: (v: string) => void;
  setTableDataInfo?: (v: string | null) => void;
  setSuccessData?: (v: string | null) => void;
  setFailedData?: (v: string | null) => void;
  setActiveStep?: (v: number) => void;
  setOrderCountInfo?: (v: string | number) => void;
  setOrderTotalValue?: (v: number | undefined) => void;
  setAmount?: (v: number) => void;
  setFromTableData?: (v: boolean) => void;
  setFixErrorTableData?: (v: string | null) => void;
  setFileState?: (v: boolean | null) => void;
}

interface HistoryRow {
  id: string;
  fileId: string;
  fileName: string;
  uploadedTimestamp: string;
  uploadedDate: string;
  uploadedBy: string;
  successCount: number;
  failCount: number;
  orderPlaced: boolean;
  subTotal: number;
  action: ComponentData | null;
  sourceFileKey?: string | null; // DV-2289: archived source sheet S3 key (order uploads)
  [key: string]: unknown;
}

interface NameOption {
  value: string;
  label: string;
}

export function useImportHistory(props: ImportHistoryProps) {
  const {
    apiResponseData,
    isProperty = false,
    setFileId,
    setUploadedByInfo,
    setFileName,
    setDateInfo,
    setTableDataInfo,
    setSuccessData,
    setFailedData,
    setActiveStep,
    setOrderCountInfo,
    setOrderTotalValue,
    setAmount,
    setFromTableData,
    setFixErrorTableData,
    setFileState
  } = props;

  const { navigateTo } = useNavigationService();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState<HistoryRow[]>([]);
  const [filters, setFilters] = useState<Filters[]>(TABLE_FILTERS);
  const [showClearFilterBtn, setShowClearFilterBtn] = useState(false);
  const [names, setNames] = useState<NameOption[]>([]);
  const [columns, setTableColumns] = useState<Columns[]>([]);
  const [dateInFilter, setDateInFilter] = useState('');

  const orderStatusRef = useRef<SimpleSelectHandle>(null);
  const uploadByRef = useRef<SimpleSelectHandle>(null);
  const dateInputElement = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setTableColumns(processTableColumns(isProperty));
    getTableDataFromAPI();
    // Mount-only initial fetch; isProperty is route-derived and stable for
    // this page's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (apiResponseData) {
      const patched = patchPropertyData(
        isProperty,
        apiResponseData
      ) as HistoryRow;
      const data: HistoryRow[] = [patched, ...tableData];
      assignAction(data[0]);
      setTableData(data);
      extractUniqueNames(data);
    }
    // Prepends a freshly-uploaded row when apiResponseData arrives. tableData
    // intentionally not in deps — the merge uses the closure's tableData and
    // re-running on every tableData change would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiResponseData]);

  // ---- Fix errors handler ----

  const onFixErrors = useCallback(
    (tableRow: HistoryRow) => {
      if (!isProperty) {
        setOrderTotalValue?.(tableRow.subTotal);
        setAmount?.(tableRow.subTotal);
        setFromTableData?.(true);
      }
      getFailedDataOfFile(tableRow);
    },
    // setOrderTotalValue / setAmount / setFromTableData / getFailedDataOfFile
    // are stable refs from props/closure; only isProperty needs to retrigger
    // the callback identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isProperty]
  );

  const getFailedDataOfFile = async (tableRow: HistoryRow) => {
    try {
      const fileData = await getFileFailureDataAPI(tableRow.id, isProperty);
      if (fileData) {
        processFailedData(fileData.data, tableRow);
      }
    } catch (err: unknown) {
      dispatch(showAlert({ type: Variants.ERROR, apiResponse: err }));
    }
  };

  const processFailedData = (
    rawData: Record<string, unknown>[],
    tableRow: HistoryRow
  ) => {
    if (isProperty) {
      const { result } = parsePropertyErrors(rawData, tableRow);
      navigateTo(ROUTES.PROPERTIES_NEW_FIX_ERROR, {
        id: tableRow.id,
        by: tableRow.uploadedBy,
        file: tableRow.fileName,
        fileId: tableRow.fileId,
        date: tableRow.uploadedDate,
        success: tableRow.successCount.toString(),
        failed: tableRow.failCount.toString(),
        tableData: JSON.stringify(result),
        isProperty: 'true'
      });
    } else {
      const { result, failedCount, successCount } = parseOrderErrors(
        rawData,
        tableRow
      );
      setFileId?.(tableRow.fileId);
      setUploadedByInfo?.(tableRow.uploadedBy);
      setFileState?.(tableRow.orderPlaced);
      setDateInfo?.(tableRow.uploadedDate);
      setFileName?.(tableRow.fileName);
      setSuccessData?.(tableRow.successCount.toString());
      setOrderCountInfo?.(successCount.toString());
      setFailedData?.(failedCount.toString());
      setFixErrorTableData?.(null);
      setTableDataInfo?.(JSON.stringify(result));
      setActiveStep?.(2);
    }
  };

  // ---- API fetch ----

  const getTableDataFromAPI = () => {
    setLoading(true);
    axios
      .get(
        getAPIUrls(
          isProperty
            ? '/property-service/api/v1/properties/bulk/requests'
            : '/order-service/api/v1/orders/bulk/requests'
        )
      )
      .then((response: { data: Record<string, unknown>[] }) => {
        const data = getTableData(
          isProperty,
          response.data
        ) as HistoryRow[];
        processActions(data);
        extractUniqueNames(data);
      })
      .catch((err: unknown) => {
        dispatch(showAlert({ type: Variants.ERROR, apiResponse: err }));
      })
      .finally(() => setLoading(false));
  };

  // ---- Table helpers ----

  const processActions = (data: HistoryRow[]) => {
    data.forEach((row) => assignAction(row));
    if (isProperty) {
      const sorted = [...data].sort((a, b) => {
        const dA = dayjs(a.uploadedTimestamp, 'MMM D, YYYY h:mm A');
        const dB = dayjs(b.uploadedTimestamp, 'MMM D, YYYY h:mm A');
        return dB.valueOf() - dA.valueOf();
      });
      setTableData(sorted);
    } else {
      setTableData(data);
    }
  };

  const makeAction = (text: string, row: HistoryRow): ComponentData => ({
    component: <States type={TableStateType.DEFAULT} text={text} onClicked={() => onFixErrors(row)} />
  });

  // DV-2289: download the archived source spreadsheet. Auth is injected by the global axios
  // interceptor; the blob is saved under the original file name.
  const downloadSourceFile = async (row: HistoryRow) => {
    try {
      const response = await axios.get(
        getAPIUrls(`/order-service/api/v1/orders/bulk/requests/${row.fileId}/file`),
        { responseType: 'blob' }
      );
      downloadBlob(response.data as Blob, row.fileName);
    } catch (err: unknown) {
      dispatch(showAlert({ type: Variants.ERROR, apiResponse: err }));
    }
  };

  const assignAction = (row: HistoryRow) => {
    if (isProperty) {
      row.action = row.failCount ? makeAction('Fix errors', row) : null;
      return;
    }
    // Order view: "Order now" (unless cleanly placed) and/or "Download" (when an archived sheet exists)
    const orderNow =
      !row.orderPlaced || row.failCount > 0 ? (
        <States
          type={TableStateType.DEFAULT}
          text="Order now"
          onClicked={() => onFixErrors(row)}
        />
      ) : null;
    const download = row.sourceFileKey ? (
      <States
        type={TableStateType.DEFAULT}
        text="Download"
        onClicked={() => downloadSourceFile(row)}
      />
    ) : null;
    row.action =
      orderNow || download
        ? {
            component: (
              <div className="flex items-center gap-4">
                {orderNow}
                {download}
              </div>
            )
          }
        : null;
  };

  const extractUniqueNames = (data: HistoryRow[]) => {
    const set = new Set<string>();
    data.forEach((d) => {
      if (d.uploadedBy != null) set.add(d.uploadedBy);
    });
    setNames([...set].map((name) => ({ value: name, label: name })));
  };

  // ---- Filtering ----

  const handleFilterChange = (key: string, value: string): void => {
    const filterBase: Filters[] = getDeepCopy(filters);
    for (const filter of filterBase) {
      if (filter.selector === key) {
        filter.value =
          key === 'uploadedDate' && value?.length > 0
            ? dayjs(value).format('LL') || ''
            : value || '';
      }
    }
    if (key === 'uploadedDate') {
      setDateInFilter(
        value.length > 0 ? dayjs(value).format('MMM D, YYYY') : ''
      );
    }
    setFilters([...filterBase]);
    const allClear = filterBase.every(
      (f) => f.selector === 'fileName' || f.value === ''
    );
    setShowClearFilterBtn(!allClear);
  };

  const clearFilter = (): void => {
    setDateInFilter('');
    setFilters(
      filters.map((f) =>
        f.selector === 'fileName' ? f : { ...f, value: '' }
      )
    );
    orderStatusRef.current?.clearValue();
    uploadByRef.current?.clearValue();
    setShowClearFilterBtn(false);
  };

  return {
    loading,
    tableData,
    filters,
    showClearFilterBtn,
    names,
    columns,
    dateInFilter,
    orderStatusRef,
    uploadByRef,
    dateInputElement,
    handleFilterChange,
    clearFilter,
    isProperty
  };
}
